import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import { useState, useCallback, useEffect } from 'react';
import cx from 'obj-str';
import { isFunction } from '@elliemae/ds-utilities/utils';
import createInstancePlugin from '@elliemae/ds-shared/createDataInstance/createInstancePlugin';
import { selectionColumnCreator } from './addSelectableColumn.js';
import { useSelectableState } from './useSelectableState.js';
import { selectMultipleRow, selectSingleRow } from './helper.js';
import '@babel/runtime/helpers/esm/objectWithoutProperties';
import '@elliemae/ds-shared/useDataGrid/initColumnDefinition';
import './selectableFormatter.js';
import '@elliemae/ds-basic/form/Checkbox';
import '@elliemae/ds-basic/form/Radio';
import '@elliemae/ds-utilities/hooks/useDerivedStateFromProps';

var decorateColumns = function decorateColumns(columns, grid) {
  var _grid$props = grid.props,
      selectable = _grid$props.selectable,
      multiSelectFilterLabel = _grid$props.multiSelectFilterLabel,
      multiSelectFilterOptions = _grid$props.multiSelectFilterOptions,
      multiSelectFilterLabels = _grid$props.multiSelectFilterLabels,
      multiSelectComponent = _grid$props.multiSelectComponent,
      _grid$actions = grid.actions,
      handleSelectAll = _grid$actions.selectAll,
      handleSelectRow = _grid$actions.selectRow,
      selection = grid.state.selection;
  var createSelectionColumn = selectionColumnCreator({
    grid: grid,
    multiple: selectable === 'multi',
    selectAll: selection.selectAll,
    onSelectAll: handleSelectAll,
    onSelectRow: handleSelectRow,
    multiSelectFilterLabel: multiSelectFilterLabel,
    multiSelectFilterOptions: multiSelectFilterOptions,
    multiSelectFilterLabels: multiSelectFilterLabels,
    multiSelectComponent: multiSelectComponent
  });
  var _grid$props2 = grid.props,
      showSelectColumn = _grid$props2.showSelectColumn,
      selectionColumnDefinition = _grid$props2.selectionColumnDefinition;

  if (showSelectColumn) {
    var selectionColumn = createSelectionColumn(selectionColumnDefinition);
    return [selectionColumn].concat(_toConsumableArray(columns));
  }

  return columns;
};

var getRowProps = function getRowProps(rowProps, grid, _ref) {
  var _cx;

  var rowData = _ref.rowData;

  var _grid$getInstance = grid.getInstance(),
      _grid$getInstance$pro = _grid$getInstance.props,
      showSelectHighlight = _grid$getInstance$pro.showSelectHighlight,
      showSelectColumn = _grid$getInstance$pro.showSelectColumn,
      rowKey = _grid$getInstance$pro.rowKey,
      selectRow = _grid$getInstance.actions.selectRow;

  var _index = rowData._index;
  var isSelected = rowData['metaData.selected'];

  if (!showSelectColumn) {
    rowProps.onClick = function () {
      return selectRow(rowData[rowKey], _index);
    };
  }

  rowProps.className = cx((_cx = {}, _defineProperty(_cx, rowProps.className, true), _defineProperty(_cx, "selected", isSelected), _defineProperty(_cx, 'select-highlight', showSelectHighlight && isSelected), _cx));
  return rowProps;
};

var composeRows = function composeRows(rows, grid) {
  var _grid$getInstance2 = grid.getInstance(),
      _grid$getInstance2$pr = _grid$getInstance2.props,
      isRowSelected = _grid$getInstance2$pr.isRowSelected,
      multiple = _grid$getInstance2$pr.multiple,
      rowKey = _grid$getInstance2$pr.rowKey,
      selection = _grid$getInstance2.state.selection;

  var selectionPredicate = function selectionPredicate(row) {
    return isFunction(isRowSelected) ? isRowSelected(row) : !!selection.selectedRows[row[rowKey]];
  };

  return multiple ? selectMultipleRow(rows, selectionPredicate) : selectSingleRow(rows, selectionPredicate);
};

var decorateGrid = function decorateGrid(grid) {
  // const handler = useRef();
  var _grid$getInstance3 = grid.getInstance(),
      _grid$getInstance3$pr = _grid$getInstance3.props,
      isRowSelected = _grid$getInstance3$pr.isRowSelected,
      rowKey = _grid$getInstance3$pr.rowKey,
      selectRow = _grid$getInstance3.actions.selectRow,
      selection = _grid$getInstance3.state.selection;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      shiftPressed = _useState2[0],
      setShiftPressed = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      lastSelectedRow = _useState4[0],
      setLastSelectedRow = _useState4[1];

  var selectRowHandler = useCallback(function (rowData, index) {
    return function () {
      return selectRow(rowData[rowKey], index, shiftPressed);
    };
  }, [isRowSelected, selection, shiftPressed]);
  useEffect(function () {
    var keyDownListener = function keyDownListener(event) {
      if (event.key === 'Shift' && event.target.tagName.toLowerCase() !== 'input' && event.target.parentNode.className !== 'em-ds-combobox__input') {
        setShiftPressed(true);
      }
    };

    var keyUpListener = function keyUpListener(event) {
      if (event.key === 'Shift' && event.target.tagName.toLowerCase() !== 'input' && event.target.parentNode.className !== 'em-ds-combobox__input') {
        setShiftPressed(false);
      }
    };

    window.addEventListener('keydown', keyDownListener);
    window.addEventListener('keyup', keyUpListener);
    return function () {
      window.removeEventListener('keydown', keyDownListener);
      window.removeEventListener('keyup', keyUpListener);
    };
  });
  return {
    shiftPressed: shiftPressed,
    selectRowHandler: selectRowHandler,
    setLastSelectedRow: setLastSelectedRow,
    lastSelectedRow: lastSelectedRow
  };
};

var SelectablePlugin = createInstancePlugin('selectable', {
  decorateGrid: decorateGrid,
  registerStateHook: useSelectableState,
  decorateColumns: decorateColumns,
  getRowProps: getRowProps,
  composeRows: composeRows,
  registerHotKeys: function registerHotKeys(grid) {
    return [{
      key: 'space',
      handler: function handler(_ref2) {
        var rowIndex = _ref2.rowIndex;

        var _grid$getInstance4 = grid.getInstance(),
            composedRows = _grid$getInstance4.composedRows,
            rowKey = _grid$getInstance4.props.rowKey,
            selectRow = _grid$getInstance4.actions.selectRow;

        var rows = composedRows.rows || composedRows;
        var row = rows[rowIndex];
        if (!row) return;
        selectRow(row[rowKey], rowIndex);
      }
    }, {
      key: 'ctrl+a',
      handler: function handler() {
        var _grid$getInstance5 = grid.getInstance(),
            selection = _grid$getInstance5.state.selection,
            selectAll = _grid$getInstance5.actions.selectAll;

        selectAll(!selection.selectAll);
      }
    }];
  }
});

export { SelectablePlugin };
//# sourceMappingURL=SelectablePlugin.js.map
